import axios from 'axios';

// Configuración centralizada de la API
const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/cognitive',
  endpoints: {
    health: '/api/v1/health',
    status: '/api/v1/status',
    metrics: '/api/v1/metrics',
    sparql: '/api/v1/sparql',
    chat: '/api/v1/chat',
    etl: {
      process: '/api/v1/etl/process',
      status: '/api/v1/etl/status'
    },
    groundTruth: '/api/v1/ground-truth',
    files: {
      list: '/api/v1/files',
      upload: '/api/v1/files/upload'
    },
    kgToText: '/api/v1/kg-to-text',
    audit: {
      config: '/api/v1/audit/config',
      stats: '/api/v1/audit/stats',
      chat: '/api/v1/audit/chat',
      llm: '/api/v1/audit/llm',
      cypher: '/api/v1/audit/cypher',
      mutations: '/api/v1/audit/mutations',
      sparql: '/api/v1/audit/sparql',
      kgToText: '/api/v1/audit/kg-to-text',
      ingestion: '/api/v1/audit/ingestion',
      traces: '/api/v1/audit/traces',
      charts: '/api/v1/audit/charts',
      aiAct: '/api/v1/audit/ai-act',
      schema: '/api/v1/audit/schema',
      lineageSearch: '/api/v1/audit/lineage/search',
      lineage: '/api/v1/audit/lineage',
      risks: '/api/v1/audit/risks',
      mappings: '/api/v1/audit/mappings',
    }
  }
};

// Persistent per-browser session id (used by audit events; not auth)
function getSessionId() {
  try {
    let sid = localStorage.getItem('neorag_session_id');
    if (!sid) {
      const cryptoObj = typeof crypto !== 'undefined' ? crypto : null;
      sid = cryptoObj?.randomUUID
        ? cryptoObj.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('neorag_session_id', sid);
    }
    return sid;
  } catch (_) {
    return '';
  }
}

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach X-Session-Id on every request so audit events can be correlated.
apiClient.interceptors.request.use((config) => {
  const sid = getSessionId();
  if (sid) {
    config.headers = config.headers || {};
    config.headers['X-Session-Id'] = sid;
  }
  return config;
});

// Interceptors para manejo de errores
apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error.response?.data || error.message);

    // Manejo específico de errores
    if (error.response?.status === 400) {
      console.warn('Bad Request - Verificar parámetros de la consulta');
    } else if (error.response?.status === 500) {
      console.error('Server Error - Problema en el backend');
    } else if (error.code === 'ECONNABORTED') {
      console.error('Timeout - La consulta tomó demasiado tiempo');
    }

    return Promise.reject(error);
  }
);

// Funciones específicas para cada endpoint
export const apiService = {
  // Health check
  async healthCheck() {
    const response = await apiClient.get(API_CONFIG.endpoints.health);
    return response.data;
  },

  // Estado del servicio
  async getStatus() {
    const response = await apiClient.get(API_CONFIG.endpoints.status);
    return response.data;
  },

  // Métricas
  async getMetrics() {
    const response = await apiClient.get(API_CONFIG.endpoints.metrics);
    return response.data;
  },

  // Consultas SPARQL
  async executeSparql(query, format = 'json', timeoutMs = 90000) {
    const response = await apiClient.post(API_CONFIG.endpoints.sparql, {
      query: query.trim(),
      format
    }, { timeout: timeoutMs });
    return response.data;
  },

  // Chat conversacional (Gemini + Cypher; hub nodes can be slow — generous timeout)
  async chatWithKnowledge(question, options = {}) {
    const body = {
      question,
      context_limit: options.contextLimit || 80,
      include_sources: options.includeSources !== false,
    };
    const fid = options.focusNodeId;
    if (fid != null && String(fid).trim() !== '') {
      body.focus_node_id = String(fid).trim();
    }
    const response = await apiClient.post(API_CONFIG.endpoints.chat, body, {
      timeout: options.timeoutMs ?? 120000,
    });
    return response.data;
  },

  // ETL processing
  async processETL(request) {
    const response = await apiClient.post(API_CONFIG.endpoints.etl.process, request);
    return response.data;
  },

  // ETL status
  async getETLStatus(jobId) {
    const response = await apiClient.get(`${API_CONFIG.endpoints.etl.status}/${jobId}`);
    return response.data;
  },

  // Ground truth mappings
  async getGroundTruthMappings() {
    const response = await apiClient.get(API_CONFIG.endpoints.groundTruth);
    return response.data;
  },


  // File management
  async getAvailableFiles() {
    const response = await apiClient.get(API_CONFIG.endpoints.files.list);
    return response.data;
  },

  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(API_CONFIG.endpoints.files.upload, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // KG-to-text article generation (LLM + graph; often > 30s — do not use default client timeout)
  async kgToText(nodeId = null, sparqlQuery = null, language = 'es') {
    const response = await apiClient.post(
      API_CONFIG.endpoints.kgToText,
      {
        node_id: nodeId,
        sparql_query: sparqlQuery,
        language,
      },
      { timeout: 180000 }
    );
    return response.data;
  },

  // --- Trazabilidad (audit read) ---
  async getAuditConfig() {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.config);
    return r.data;
  },
  async getAuditStats() {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.stats);
    return r.data;
  },
  async listAuditChat(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.chat, { params });
    return r.data;
  },
  async listAuditLLM(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.llm, { params });
    return r.data;
  },
  async listAuditCypher(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.cypher, { params });
    return r.data;
  },
  async listAuditMutations(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.mutations, { params });
    return r.data;
  },
  async listAuditSparql(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.sparql, { params });
    return r.data;
  },
  async listAuditKgToText(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.kgToText, { params });
    return r.data;
  },
  async listAuditIngestion(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.ingestion, { params });
    return r.data;
  },
  async listAuditTraces(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.traces, { params });
    return r.data;
  },
  async getAuditTrace(traceId) {
    const r = await apiClient.get(`${API_CONFIG.endpoints.audit.traces}/${encodeURIComponent(traceId)}`);
    return r.data;
  },
  async verifyAuditTrace(traceId) {
    const r = await apiClient.get(`${API_CONFIG.endpoints.audit.traces}/${encodeURIComponent(traceId)}/verify`);
    return r.data;
  },
  async exportAuditTrace(traceId) {
    const r = await apiClient.get(`${API_CONFIG.endpoints.audit.traces}/${encodeURIComponent(traceId)}/export`);
    return r.data;
  },
  async getAuditChart(metric, params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.charts, { params: { metric, ...params } });
    return r.data;
  },
  async getAuditAIAct() {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.aiAct);
    return r.data;
  },
  async getAuditSchema() {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.schema);
    return r.data;
  },
  async searchAuditLineage(params = {}) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.lineageSearch, { params });
    return r.data;
  },
  async getAuditLineage(label, mergeValue) {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.lineage, {
      params: { label, merge_value: mergeValue },
    });
    return r.data;
  },
  async getAuditRisks() {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.risks);
    return r.data;
  },
  async listAuditMappings() {
    const r = await apiClient.get(API_CONFIG.endpoints.audit.mappings);
    return r.data;
  },
  async getAuditMapping(name) {
    const r = await apiClient.get(
      `${API_CONFIG.endpoints.audit.mappings}/${encodeURIComponent(name)}`
    );
    return r.data;
  },
};

export { API_CONFIG };
export default apiClient;
